import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook per gestire connessioni WebSocket per alert di sicurezza in tempo reale
 * 
 * @example
 * const { connected, alerts, latestAlert, clearAlerts } = useSecurityAlerts(token);
 */
export function useSecurityAlerts(token) {
  const [connected, setConnected] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [latestAlert, setLatestAlert] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!token) return;

    // Costruisci URL WebSocket
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${protocol}//${host}/api/ws/alerts?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'security_alert') {
            const newAlert = {
              id: Date.now(),
              ...data,
              read: false
            };
            setAlerts(prev => [newAlert, ...prev].slice(0, 100)); // Max 100 alert
            setLatestAlert(newAlert);
          } else if (data.type === 'connected') {
            // Connection confirmed
          }
        } catch (e) {
          // Ignora messaggi non-JSON
        }
      };

      ws.onclose = (event) => {
        setConnected(false);
        wsRef.current = null;

        // Tenta riconnessione se non è una chiusura intenzionale
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // Error handling - will trigger onclose
      };

      wsRef.current = ws;
    } catch (e) {
      // WebSocket creation failed
    }
  }, [token]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    };
  }, [connect]);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
    setLatestAlert(null);
  }, []);

  const markAsRead = useCallback((alertId) => {
    setAlerts(prev => prev.map(a => 
      a.id === alertId ? { ...a, read: true } : a
    ));
  }, []);

  const markAllAsRead = useCallback(() => {
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
  }, []);

  const unreadCount = alerts.filter(a => !a.read).length;

  return {
    connected,
    alerts,
    latestAlert,
    unreadCount,
    clearAlerts,
    markAsRead,
    markAllAsRead
  };
}

export default useSecurityAlerts;
